import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../../components/common/NotificationSystem';
import { authService } from '../../services/authService';
import Logo from '../../components/common/Logo';
import './Auth.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [errors, setErrors] = useState({});
  const { showSuccess, showError } = useNotifications();

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors(prev => ({
        ...prev,
        email: ''
      }));
    }
  };

  const validateEmail = () => {
    if (!email) {
      setErrors({ email: 'Email is required' });
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: 'Please enter a valid email' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Use authService for password reset request
      const response = await authService.requestPasswordReset(email);

      if (response.success) {
        setIsEmailSent(true);
        showSuccess('Password reset email sent successfully', 'Email Sent');
      } else {
        setErrors({ general: response.message || 'Failed to send reset email. Please try again.' });
        showError(response.message || 'Failed to send reset email', 'Error');
      }

    } catch (error) {
      setErrors({ general: error.message || 'Failed to send reset email. Please try again.' });
      showError('Failed to send reset email', 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>
            <h1>Check Your Email</h1>
            <p>We've sent password reset instructions to</p>
            <p className="email-display">{email}</p>
          </div>

          <div className="success-message">
            <p>Please check your email and follow the instructions to reset your password.</p>
            <p>If you don't see the email, check your spam folder.</p>
          </div>

          <div className="auth-footer">
            <button
              className="auth-button secondary"
              onClick={() => setIsEmailSent(false)}
            >
              Try Different Email
            </button>
            <p><Link to="/login">Back to Sign In</Link></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Logo
            className="auth-logo"
            alt="AFF Logo"
          />
          <h1>Reset Password</h1>
          <p>Enter your email address and we'll send you instructions to reset your password</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
              autoComplete="email"
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Instructions'}
          </button>
        </form>

        <div className="auth-footer">
          <p><Link to="/login">Back to Sign In</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
